import { isE2E, isNewIvsE2EDevice, isJSONString, convertString2JSON } from "utils";
import { getInteractor } from "services/local.service";
import { SystemConstant } from "const";
import { updateStatusWithSourceMes } from "sagas/conversation-message.saga";
import { restoreResetMessageToLocal } from "pubsub/services/message/getMessage";
import { toCamel } from "utils";

const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;

class EncryptionErrorNotifier {
  constructor(decryptNormalService) {
    this.decryptNormalService = decryptNormalService;
  }

  async handleErrorMessage(message, group, device, messageOptions, encryptTypeProtocol, prefixKey) {
    if (isNewIvsE2EDevice(device)) {
      message.sendType = messageOptions.original_send_type ?? SystemConstant.SEND_TYPE.keyError;
    }

    let isSupportResendE2E = false;
    if (messageOptions?.is_resend_e2e != null) {
      isSupportResendE2E = messageOptions.is_resend_e2e;
    }
    if (!isSupportResendE2E) {
      await this.resetWaitingIndex(message, group, encryptTypeProtocol, prefixKey);
    }

    let encryptStatusInfo;
    switch (message.sendType) {
      case SystemConstant.SEND_TYPE.keyError:
        encryptStatusInfo = await this.#decryptMessage({
          message: message,
          group: group,
          messageOptions: messageOptions,
          prefixKey: prefixKey,
          indexNumber: messageOptions?.index_send_number,
          isResendE2E: isSupportResendE2E,
        });
        break;
      default:
        encryptStatusInfo = {
          type: SystemConstant.ENCRYPT_STATUS_TYPE.error,
          code: SystemConstant.ENCRYPT_STATUS_CODE.errorCommon,
        };
        break;
    }

    return encryptStatusInfo;
  }

  #decryptMessage = async ({ message, group, messageOptions, prefixKey, indexNumber, isResendE2E }) => {
    let encryptStatusInfo = await this.decryptNormalService.decryptNormalMessage(
      message,
      group,
      messageOptions,
      prefixKey,
      indexNumber,
    );

    if (encryptStatusInfo?.type === SystemConstant.ENCRYPT_STATUS_TYPE.success && isJSONString(message.content)) {
      let resendMessages = JSON.parse(message.content).messages;
      await restoreResetMessageToLocal(resendMessages, prefixKey);

      /// Fir resendE2E case, we need to call api update status by source id
      if (isResendE2E) {
        for (const resendMessage of resendMessages) {
          const resendMesOptions = convertString2JSON(resendMessage.options, {});
          const encryptVersion = resendMesOptions.encrypt_version;
          const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);

          await this.validateWaitingIndex({
            prefixKey: prefixKey,
            message: toCamel(resendMessage),
            group: group,
            encryptTypeProtocol: encryptTypeProtocol,
            resendIndexNumber: resendMesOptions?.index_send_number,
          });
          await updateStatusWithSourceMes({
            data: {
              sourceId: resendMessage.sourced_id,
              status: SystemConstant.MESSAGE_STATUS.read,
            },
            prefixKey,
          });
        }
      }
    }

    return encryptStatusInfo;
  };

  /**
   *
   * @param message
   * @param group
   * @param encryptTypeProtocol
   * @param {string} prefixKey
   * @returns {Promise<void>}
   */
  async resetWaitingIndex(message, group, encryptTypeProtocol, prefixKey) {
    if (!group) return;
    const updateValues = { waiting_index: -1 };
    if (isE2E(message.sendType, group.groupType)) {
      await getInteractor(prefixKey).LocalCryptoService.updateSession(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        encryptTypeProtocol,
      );
    } else {
      await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
        updateValues,
        message.senderId,
        message.senderDeviceId,
        message.groupId,
        encryptTypeProtocol,
      );
    }
  }

  async validateWaitingIndex({ prefixKey, message, group, encryptTypeProtocol, resendIndexNumber }) {
    if (!group) return;
    let currentWaitingIndex = await this.decryptNormalService.getWaitingIndex(
      prefixKey,
      group,
      message,
      encryptTypeProtocol,
    );
    if (currentWaitingIndex === resendIndexNumber) {
      const updateValues = { waiting_index: resendIndexNumber + 1 };
      if (isE2E(message.sendType, group.groupType)) {
        await getInteractor(prefixKey).LocalCryptoService.updateSession(
          updateValues,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          encryptTypeProtocol,
        );
      } else {
        await getInteractor(prefixKey).LocalCryptoService.updateSenderKey(
          updateValues,
          message.senderId,
          message.senderDeviceId,
          message.groupId,
          encryptTypeProtocol,
        );
      }
    }
  }
}

export default EncryptionErrorNotifier;
